import React, { useState, useEffect, ChangeEvent, useCallback } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import debounce from 'lodash.debounce';
import apiClient from 'apiClient';
import { advisers, registerPersonalDetails } from 'routes/routePaths';
import { getVerifyStrategyFactoryMethod } from 'utils/request/lists';
import List from 'components/List/List';
import { AdviserResponseData, Data } from './interface';
import { Row } from 'components/List/types';
import SearchIcon from 'shared/Icons/SearchIcon/SearchIcon';
import ErrorManager from 'shared/Messages/ErrorManager';
import { mapAdvisersData } from './utils';
import useBodyClass from 'helpers/hooks/useBodyClass';
import { setContextFirm, setSearchFirmsForm } from 'screens/BrokerPortal/screens/Registration/store/actions';
import { FIELD } from 'screens/BrokerPortal/screens/Registration/components/SearchFirmsStep/types';
import { useDispatch } from 'react-redux';
import { CompanyDetails } from './interface';

interface RouteProps {
  id: string;
}

const initialState: CompanyDetails = {
  fca: { number: '' },
  name: '',
};

const { details: adviserDetailsPage } = advisers;

const AdvisersListPage: React.FC<RouteComponentProps<RouteProps>> = ({ history, match }) => {
  const [advisers, setAdvisers] = useState<AdviserResponseData>([]);
  const [companyDetails, setCompanyDetails] = useState<CompanyDetails>(initialState);
  const [shouldShowInput, setShouldShowInput] = useState<boolean>(false);
  const [query, setQuery] = useState<string>('');
  const [error, setError] = useState<string>('');
  const dispatch = useDispatch();
  const { id } = match.params;
  const url = `/api/v1/broker-management/firms/${id}/advisers/`;
  useBodyClass('light');

  useEffect(() => {
    let shouldCancel = false;

    const getAdvisersList = async () => {
      try {
        const res = await apiClient.get(url);
        if (!shouldCancel) setAdvisers(mapAdvisersData(res.data));
      } catch (error) {
        if (!shouldCancel) setError(error.response);
      }
    };

    getAdvisersList();

    return () => {
      shouldCancel = true;
    };
  }, [id, url]);

  useEffect(() => {
    let shouldCancel = false;

    const getFirmDetails = async () => {
      const res = await apiClient.get(`/api/v1/broker-management/firms-short/${id}`);
      if (!shouldCancel) setCompanyDetails(res.data);
    };

    getFirmDetails();

    return () => {
      shouldCancel = true;
    };
  }, [id]);

  const handleIconClick = () => setShouldShowInput((state) => !state);

  const fetchAdvisers = async (value: string) => {
    const param = value ? `?name=${value}` : '';
    const res = await apiClient.get(`${url}${param}`);
    setAdvisers(mapAdvisersData(res.data));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceFetchAdvisers = useCallback(debounce(fetchAdvisers, 200), []);

  const handleSearch = async (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setQuery(value);
    debounceFetchAdvisers(value);
  };

  const prepareRows = (items: AdviserResponseData): Row[] => {
    return items.map((item) => ({
      key: item.id,
      data: [
        { label: item.fullName, link: { href: `${adviserDetailsPage}/${item.id}` } },
        { label: item.email },
        { label: item.fca.number },
        { label: item.name },
        { label: item.fca_status ? 'Active' : 'Inactive' },
        { label: item.lmc_status ? 'Active' : 'Inactive' },
        { label: item.fca_status ? 'Deactivate' : 'Activate', action: () => ({ item, status: 'fca_status' }) },
        { label: item.lmc_status ? 'Deactivate' : 'Activate', action: () => ({ item, status: 'lmc_status' }) },
      ],
    }));
  };

  const updateStatus = async ({ item, status }: { item: Data; status: 'fca_status' | 'lmc_status' }) => {
    try {
      const newAdvisers = [...advisers] as any;
      const index = advisers.findIndex((advisersData: Data) => advisersData.id === item.id);
      const newStatus = !newAdvisers[index][status];
      const verifyStrategy = getVerifyStrategyFactoryMethod('adviser', status)(item, newStatus);
      await apiClient.post(verifyStrategy.endpoint, verifyStrategy.body);

      if (index !== -1) {
        newAdvisers[index][status] = newStatus;
        setAdvisers(newAdvisers);
      }
    } catch (err) {
      const message = err.message || err.response;
      console.log('Could not change status.'); // TO DO notify user of the error
      console.warn(message);
    }
  };

  const redirectToCreatePage = () => {
    dispatch(
      setSearchFirmsForm({
        [FIELD.FIRM_ID]: Number(id),
      }),
    );
    dispatch(
      setContextFirm({
        id: Number(id),
        fcaNumber: companyDetails.fca.number,
        name: companyDetails.name,
      }),
    );
    history.push(registerPersonalDetails);
  };

  const icon = (
    <button className="transparent list__search-button" onClick={handleIconClick}>
      <SearchIcon />
    </button>
  );

  return (
    <>
      <List
        labels={[
          'Adviser Name',
          'Email',
          'Adviser FCA Number',
          'Firm Name',
          'FCA Status',
          'LMC Status',
          'FCA Activation',
          'LMC Activation',
        ]}
        icons={[icon]}
        rows={prepareRows(advisers)}
        listName={companyDetails.name}
        subtitle={companyDetails.fca.number && `FCA Registration Number: ${companyDetails.fca.number}`}
        onAction={updateStatus}
        addNewLabel="Add Adviser"
        onAddNewItem={redirectToCreatePage}
        search={{
          isPerformed: shouldShowInput,
          placeholder: 'Search Adviser Name',
          query: query,
          onSearch: handleSearch,
        }}
      />
      {error && <ErrorManager error={error} />}
    </>
  );
};

export default AdvisersListPage;
